import React, { useState, useEffect, useRef } from "react";
import {
  Select,
  Input,
  Spin,
  Alert,
  Card,
  Row,
  Col,
  Typography,
  Empty,
  Space,
  Button,
  Modal,
} from "antd";
import { FileExcelOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import { getinduk, getmasteraset } from "../api/api";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import "./MasterAset.css";

const { Title } = Typography;
const { Option } = Select;

const API_BASE = process.env.REACT_APP_API_BASE || "";

/**
 * LazyImage (modified): langsung render <img> tanpa placeholder loading.
 * - tetap mempertahankan ukuran kontainer untuk menghindari layout shift.
 * - memoized untuk stabilitas.
 */
const LazyImage = React.memo(function LazyImage({ src, alt, fallbackSrc = null, style = {}, onClick }) {
  const imgRef = useRef(null);
  const [currentSrc, setCurrentSrc] = useState(src || fallbackSrc || null);

  // update src when props change
  useEffect(() => {
    setCurrentSrc(src || fallbackSrc || null);
  }, [src, fallbackSrc]);

  const containerStyle = {
    width: "100%",
    height: 150,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    overflow: "hidden",
    cursor: onClick ? "pointer" : "default",
    ...style,
  };

  return (
    <div style={containerStyle} onClick={onClick}>
      {currentSrc ? (
        <img
          ref={imgRef}
          src={currentSrc}
          alt={alt}
          decoding="async"
          loading="lazy"
          width="100%"
          height="150"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={(e) => {
            try {
              const fb = fallbackSrc;
              if (fb && e.currentTarget.src !== fb) {
                e.currentTarget.src = fb;
              } else {
                e.currentTarget.src = "data:image/gif;base64,R0lGODlhAQABAPAAAP///wAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";
              }
            } catch (err) {
              /* silent */
            }
          }}
        />
      ) : (
        <div style={{ height: 150, width: "100%", background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: 12 }}>
          No Image
        </div>
      )}
    </div>
  );
});

const MasterAset = () => {
  const [cabangs, setCabangs] = useState([]);
  const [selectedCabang, setSelectedCabang] = useState(() => localStorage.getItem("selectedCabang") || null);
  const [asetList, setAsetList] = useState([]);
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("asetSearch") || "");
  const [sortOption, setSortOption] = useState(null);
  const [selectedKategori, setSelectedKategori] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isScrolled, setIsScrolled] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // preview modal
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [previewAlt, setPreviewAlt] = useState("");
  const asetRefForPreview = useRef(null); // keep reference to asset object for fallback use

  // refs for scroll listener ticking/lastY
  const tickingRef = useRef(false);
  const lastYRef = useRef(0);

  useEffect(() => {
    const saved = localStorage.getItem("selectedCabang");
    if (saved) setSelectedCabang(saved);
  }, []);

  useEffect(() => {
    if (selectedCabang) localStorage.setItem("selectedCabang", selectedCabang);
  }, [selectedCabang]);

  useEffect(() => {
    localStorage.setItem("asetSearch", searchTerm);
  }, [searchTerm]);

  // load cabang list
  useEffect(() => {
    let mounted = true;
    getinduk()
      .then((data) => {
        if (!mounted) return;
        if (data?.status === "error") {
          setError(data.message || "Gagal mengambil cabang");
          setCabangs([]);
        } else {
          if (Array.isArray(data)) setCabangs(data);
          else if (Array.isArray(data?.data)) setCabangs(data.data);
          else setCabangs(Array.isArray(data) ? data : []);
          setError(null);
        }
      })
      .catch((err) => {
        if (!mounted) return;
        console.error("getinduk error:", err);
        setError("Gagal mengambil data cabang");
        setCabangs([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // helper: detect if r.gambar should be treated as base64
  const isBase64String = (str) => {
    if (!str || typeof str !== "string") return false;
    if (str.startsWith("data:")) return true;
    const maybeBase64 = /^[A-Za-z0-9+/=]+$/.test(str.replace(/\s+/g, ""));
    return maybeBase64 && str.length > 100;
  };

  // load aset per cabang
  useEffect(() => {
    if (!selectedCabang) {
      setAsetList([]);
      return;
    }

    // clear current aset list quickly so UI shows immediate refresh
    setAsetList([]);
    setSelectedKategori(null);
    setLoading(true);
    setError(null);

    let mounted = true;
    getmasteraset(selectedCabang)
      .then((data) => {
        if (!mounted) return;
        if (Array.isArray(data)) {
          const normalized = data.map((r) => {
            const gambarRaw = r.gambar;
            const isBase64 = isBase64String(gambarRaw);
            let gambarBase64 = null;
            let imageUrl = null;
            if (isBase64) {
              gambarBase64 = gambarRaw.startsWith("data:") ? gambarRaw.replace(/^data:image\/\w+;base64,?/, "") : gambarRaw;
              imageUrl = null;
            } else if (gambarRaw) {
              imageUrl = `${API_BASE}/images/${encodeURIComponent(r.kode_asset)}?size=thumb&format=webp`;
            } else {
              imageUrl = null;
            }
            return {
              ...r,
              gambarBase64,
              imageUrl,
            };
          });
          setAsetList(normalized);
          setError(null);
          const container = document.querySelector(".master-container");
          if (container) container.scrollTo({ top: 0, behavior: "smooth" });
          else window.scrollTo({ top: 0, behavior: "smooth" });
        } else if (data?.message === "Data masih kosong") {
          setAsetList([]);
          setError("Tidak ada data");
        } else if (data?.status === "error") {
          setAsetList([]);
          setError(data.message || "Error server");
        } else {
          setAsetList([]);
          setError("Data tidak valid");
        }
      })
      .catch((err) => {
        if (!mounted) return;
        console.error("getmasteraset error:", err);
        setError("Gagal mengambil data aset");
        setAsetList([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [selectedCabang]);

  // scroll listener: sticky + hide filter + show scroll top
  useEffect(() => {
    const container = document.querySelector(".master-container");
    const target = container || window;

    const handleScroll = () => {
      const cur = container && typeof container.scrollTop === "number" ? container.scrollTop : window.scrollY;

      if (!tickingRef.current) {
        tickingRef.current = true;
        window.requestAnimationFrame(() => {
          const lastY = lastYRef.current;

          if ((cur > 5) !== isScrolled) setIsScrolled(cur > 5);
          if ((cur > 250) !== showScrollTop) setShowScrollTop(cur > 250);

          if (cur > lastY + 10 && cur > 120) {
            if (showFilter) setShowFilter(false);
          } else if (cur < lastY - 10) {
            if (!showFilter) setShowFilter(true);
          }

          lastYRef.current = cur <= 0 ? 0 : cur;
          tickingRef.current = false;
        });
      }
    };

    target.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      target.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrolled, showScrollTop, showFilter]);

  const categories = Array.isArray(asetList) ? [...new Set(asetList.map((a) => a.kategori).filter(Boolean))] : [];

  let filteredAset = Array.isArray(asetList)
    ? asetList.filter((aset) => {
      const q = (searchTerm || "").toLowerCase();
      const matchesSearch =
        (aset.kode_asset || "").toLowerCase().includes(q) ||
        (aset.nm_asset || "").toLowerCase().includes(q);
      const matchesKategori = !selectedKategori || aset.kategori === selectedKategori;
      return matchesSearch && matchesKategori;
    })
    : [];

  if (sortOption) {
    filteredAset = [...filteredAset].sort((a, b) => {
      switch (sortOption) {
        case "nama-asc":
          return (a.nm_asset || "").localeCompare(b.nm_asset || "");
        case "harga-asc":
          return Number(a.harga || 0) - Number(b.harga || 0);
        case "harga-desc":
          return Number(b.harga || 0) - Number(a.harga || 0);
        case "tgl-asc":
          return new Date(a.tgl_beli) - new Date(b.tgl_beli);
        case "tgl-desc":
          return new Date(b.tgl_beli) - new Date(a.tgl_beli);
        default:
          return 0;
      }
    });
  }

  // resolve image src: prefer base64, fallback to image endpoint
  const resolveImageSrc = (aset, { size = "thumb", format = "webp" } = {}) => {
    if (!aset) return null;
    if (aset.gambarBase64) return `data:image/jpeg;base64,${aset.gambarBase64}`;
    if (aset.gambar && typeof aset.gambar === "string" && aset.gambar.startsWith("data:")) return aset.gambar;
    if (aset.imageUrl) {
      if (/^(https?:|data:)/.test(aset.imageUrl)) return aset.imageUrl;
      if (API_BASE) return `${API_BASE}${aset.imageUrl}`;
      if (aset.imageUrl.startsWith("/")) return `${window.location.origin}${aset.imageUrl}`;
      return aset.imageUrl;
    }
    if (aset.kode_asset) {
      const base = API_BASE || window.location.origin;
      return `${base}/images/${encodeURIComponent(aset.kode_asset)}?size=${size}&format=${format}`;
    }
    return null;
  };

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("MASTER ASET");
    worksheet.columns = [
      { header: "Kode Aset", key: "kode_asset", width: 20 },
      { header: "Nama Aset", key: "nm_asset", width: 40 },
      { header: "Kategori", key: "kategori", width: 15 },
      { header: "Qty", key: "qty", width: 5 },
      { header: "Harga", key: "harga", width: 15 },
      { header: "Tanggal Beli", key: "tgl_beli", width: 15 },
      { header: "Nama Cabang", key: "nm_induk", width: 30 },
      { header: "Lokasi", key: "nm_lokasi", width: 15 },
      { header: "Keterangan", key: "keterangan", width: 60 },
      { header: "Gambar", key: "gambar", width: 13 },
    ];

    for (let i = 0; i < filteredAset.length; i++) {
      const aset = filteredAset[i];
      const rowNumber = worksheet
        .addRow({
          kode_asset: aset.kode_asset,
          nm_asset: aset.nm_asset,
          kategori: aset.kategori,
          qty: aset.qty,
          harga: aset.harga,
          tgl_beli: aset.tgl_beli ? new Date(aset.tgl_beli).toLocaleDateString() : "",
          nm_induk: aset.nm_induk,
          nm_lokasi: aset.nm_lokasi,
          keterangan: aset.keterangan,
        })
        .number;

      const base64 = aset.gambarBase64 || aset.gambar || null;
      if (base64) {
        try {
          const base64Data = base64.replace(/^data:image\/\w+;base64,?/, "");
          const buffer = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
          const imageId = workbook.addImage({ buffer, extension: "jpeg" });
          worksheet.addImage(imageId, { tl: { col: 9, row: rowNumber - 1 }, ext: { width: 100, height: 100 } });
          worksheet.getRow(rowNumber).height = 58;
        } catch (err) {
          console.warn("Embed image failed for", aset.kode_asset, err);
        }
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `MasterAset_${selectedCabang || "ALL"}.xlsx`);
  };

  // Click to preview image: prefer base64, fallback to image endpoint
  const handleImageClick = (aset) => {
    asetRefForPreview.current = aset;
    const base64 = aset.gambarBase64 || aset.gambar || null;
    if (base64) {
      setPreviewSrc(base64.startsWith("data:") ? base64 : `data:image/jpeg;base64,${base64}`);
      setPreviewAlt(aset.nm_asset || aset.kode_asset || "Preview");
      setPreviewVisible(true);
      return;
    }
    const url = resolveImageSrc(aset, { size: "full", format: "webp" });
    if (url) {
      setPreviewSrc(url);
      setPreviewAlt(aset.nm_asset || aset.kode_asset || "Preview");
      setPreviewVisible(true);
      return;
    }
    setPreviewSrc(null);
    setPreviewAlt("");
    setPreviewVisible(true);
  };

  const scrollToTop = () => {
    const container = document.querySelector(".master-container");
    if (container) container.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="master-container">
      <Title level={3} className="page-title">
        MASTER ASET
      </Title>

      <div className={`filter-bar ${isScrolled ? "scrolled" : ""} ${showFilter ? "show" : "hide"}`}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Pilih Cabang"
              value={selectedCabang}
              onChange={(val) => {
                setSelectedCabang(val || null);
              }}
              style={{ width: "100%" }}
              allowClear
            >
              {cabangs.map((cabang) => (
                <Option key={cabang} value={cabang}>
                  {cabang}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter Kategori"
              value={selectedKategori}
              onChange={setSelectedKategori}
              style={{ width: "100%" }}
              allowClear
              disabled={!selectedCabang || loading}
            >
              {categories.map((kategori) => (
                <Option key={kategori} value={kategori}>
                  {kategori}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Input.Search placeholder="Cari kode atau nama aset..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} allowClear />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Select placeholder="Urutkan" style={{ width: "100%" }} value={sortOption || undefined} onChange={setSortOption} allowClear>
              <Option value="nama-asc">Nama Aset (A–Z)</Option>
              <Option value="harga-asc">Harga Termurah</Option>
              <Option value="harga-desc">Harga Termahal</Option>
              <Option value="tgl-asc">Tanggal Lama → Baru</Option>
              <Option value="tgl-desc">Tanggal Baru → Lama</Option>
            </Select>
          </Col>

          {filteredAset.length > 0 && (
            <Col xs={24} style={{ marginTop: 10, textAlign: "right" }}>
              <Space>
                <Button type="primary" icon={<FileExcelOutlined />} onClick={handleExportExcel} style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}>
                  Export Excel
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      )}
      {error && !loading && (
        <Alert message="Info" description={error} type="error" showIcon style={{ marginBottom: 20 }} />
      )}
      {!loading && filteredAset.length === 0 && !error && <Empty description="Tidak ada data aset" />}

      <Row gutter={[12, 12]}>
        {filteredAset.map((aset) => {
          const imgSrc = resolveImageSrc(aset);
          return (
            <Col xs={12} sm={8} md={6} lg={4} key={aset.kode_asset}>
              <Card
                hoverable
                className="asset-card-hover"
                style={{ borderRadius: 10, boxShadow: "0 2px 6px rgba(0,0,0,0.06)" }}
                cover={
                  imgSrc ? (
                    <div style={{ height: 150, borderBottom: "1px solid #f0f0f0" }}>
                      <LazyImage src={imgSrc} fallbackSrc={aset.gambarBase64 ? `data:image/jpeg;base64,${aset.gambarBase64}` : null} alt={aset.nm_asset} onClick={() => handleImageClick(aset)} style={{ height: 150 }} />
                    </div>
                  ) : (
                    <div style={{ height: 150, background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: 12 }}>
                      No Image
                    </div>
                  )
                }
              >
                <Card.Meta
                  title={<div style={{ whiteSpace: "normal", wordBreak: "break-word", fontWeight: 600, fontSize: 12 }}>{aset.nm_asset}</div>}
                  description={
                    <div style={{ fontSize: 12, lineHeight: 1.4 }}>
                      <div>Kode: {aset.kode_asset}</div>
                      <div>Kategori: {aset.kategori}</div>
                      <div>Qty: {aset.qty}</div>
                      <div>Harga: Rp {Number(aset.harga || 0).toLocaleString()}</div>
                      <div>Tgl Beli: {aset.tgl_beli ? new Date(aset.tgl_beli).toLocaleDateString() : "-"}</div>
                      <div>Lokasi: {aset.nm_lokasi}</div>
                      <div style={{ marginTop: 6 }}>Keterangan: {aset.keterangan || "-"}</div>
                    </div>
                  }
                />
              </Card>
            </Col>
          );
        })}
      </Row>

      <AnimatePresence>
        {showScrollTop && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.25 }}
            onClick={scrollToTop}
            style={{
              position: "fixed",
              bottom: 30,
              right: 30,
              background: "#1890ff",
              color: "#fff",
              borderRadius: "50%",
              padding: 12,
              cursor: "pointer",
              boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            whileHover={{ scale: 1.08 }}
          >
            <ArrowUpOutlined style={{ fontSize: 18 }} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview modal with zoom */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => {
          setPreviewVisible(false);
          asetRefForPreview.current = null;
        }}
        centered
        width="auto"
        style={{
          padding: 0,
        }}
        styles={{
          body: {
            padding: 0,
            margin: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "40vw",
            maxWidth: "40vw",
            minHeight: "40vh",
            maxHeight: "40vh",
          },
          header: { display: "none" },
          footer: { display: "none" },
        }}
      >
        {previewSrc ? (
          <div
            style={{
              width: "40vw",
              height: "40vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <Zoom>
              <img
                src={previewSrc}
                alt={previewAlt}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
                onError={(e) => {
                  try {
                    const el = e.currentTarget;
                    const asetCandidate = asetRefForPreview.current;
                    const fallbackBase64 = asetCandidate?.gambarBase64 ? `data:image/jpeg;base64,${asetCandidate.gambarBase64}` : null;
                    if (fallbackBase64 && el.src !== fallbackBase64) {
                      el.onerror = null;
                      el.src = fallbackBase64;
                    } else {
                      el.onerror = null;
                      el.src = "data:image/gif;base64,R0lGODlhAQABAPAAAP///wAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";
                    }
                  } catch (err) { }
                }}
              />
            </Zoom>
          </div>
        ) : (
          <Empty description="Tidak ada gambar" />
        )}
      </Modal>
    </div>
  );
};

export default MasterAset;
